/**
 * Field Map — the numbers behind the charts.
 *
 * The server already says, for every catalog field, whether the dossier holds
 * it and, when it does not, the one resolution that explains the blank. The
 * charts need those answers grouped, so this file groups them. It computes
 * nothing the payload does not contain: every count here is a count of
 * `fields[]`, and the score the server reports is carried through unchanged
 * for the figures that are its own (the per-page share, the gate).
 *
 * A "bucket" is what a reader sees: eight mutually exclusive answers to "why is
 * this cell the way it is". The mapping from the server's ten resolutions is
 * deliberately many-to-one and total: a resolution this file has never heard of
 * lands in `unchecked`, so a new server-side resolution can never vanish from a
 * chart.
 */

import type { FieldMap, FieldMapOwner, FieldMapResolution, FieldMapRow } from '@/services/loans'

export type BucketId =
  | 'shown'
  | 'absent'
  | 'derivable'
  | 'review'
  | 'miss'
  | 'desk'
  | 'na'
  | 'unchecked'

/** Reading order: held first, then the blanks by how close each is to filling. */
export const BUCKET_ORDER: readonly BucketId[] = [
  'shown',
  'absent',
  'derivable',
  'review',
  'miss',
  'desk',
  'na',
  'unchecked',
]

export type BucketCounts = Record<BucketId, number>

type BucketDef = {
  label: string
  /** The chip in the table, where the column is narrow. */
  short: string
  /** One sentence for the tooltip and "How to read this". */
  hint: string
  color: string
  /** Text on the colour: dark on the light fills, light on the dark ones. */
  ink: string
  /** A pattern laid over the colour, so no bucket is told apart by colour alone. */
  image?: string
  size?: string
}

const STRIPE = (deg: number) => `repeating-linear-gradient(${deg}deg, rgba(0,0,0,.34) 0 2px, transparent 2px 6px)`
const DOTS = 'radial-gradient(rgba(0,0,0,.42) 1px, transparent 1.5px)'

export const BUCKETS: Record<BucketId, BucketDef> = {
  shown: {
    label: 'Shown',
    short: 'Shown',
    hint: 'The dossier holds a value for this field.',
    color: '#34d399',
    ink: '#04130d',
  },
  absent: {
    label: 'Not in the package',
    short: 'Not in package',
    hint: 'Every stored source was read in full and none states it. The broker has to supply it.',
    color: '#f0d894',
    ink: '#1a1405',
  },
  derivable: {
    label: 'Derivable once inputs arrive',
    short: 'Derivable',
    hint: 'No source states it, but a formula fills it once the figures it needs are known.',
    color: '#a78bfa',
    ink: '#120a2b',
    image: STRIPE(45),
  },
  review: {
    label: 'Waiting on a person',
    short: 'Waiting on a person',
    hint: 'Sources disagree, or a value sits under a label several fields share. A reviewer decides.',
    color: '#38bdf8',
    ink: '#04131c',
    image: DOTS,
    size: '5px 5px',
  },
  miss: {
    label: 'Engine miss',
    short: 'Engine miss',
    hint: 'A source states it, or could not be searched, and the dossier does not hold it. The engine is at fault, not the package.',
    color: '#f43f5e',
    ink: '#1c0309',
    image: `${STRIPE(45)}, ${STRIPE(-45)}`,
  },
  desk: {
    label: 'Desk sets it',
    short: 'Desk sets',
    hint: 'The desk enters this field; no package states it.',
    color: '#a2a2a2',
    ink: '#0a0a0a',
    image: 'repeating-linear-gradient(90deg, rgba(0,0,0,.34) 0 1px, transparent 1px 4px)',
  },
  na: {
    label: 'Not applicable',
    short: 'N/A',
    hint: 'This asset class cannot have it, or a reviewer closed it.',
    color: '#4a4a4a',
    ink: '#e1e1e1',
  },
  unchecked: {
    label: 'Not checked',
    short: 'Not checked',
    hint: 'No package is attached, or the package search failed, so nothing was searched. A blank here is an entry gap, not a verdict on the package.',
    color: '#6b6b6b',
    ink: '#f3f3f3',
    image: DOTS,
    size: '6px 6px',
  },
}

/** The bucket the worklist's owner pill shares its colour with. */
export const OWNER_BUCKET: Record<FieldMapOwner, BucketId> = {
  broker: 'absent',
  inputs: 'derivable',
  reviewer: 'review',
  us: 'miss',
  desk: 'desk',
}

export function emptyCounts(): BucketCounts {
  return { shown: 0, absent: 0, derivable: 0, review: 0, miss: 0, desk: 0, na: 0, unchecked: 0 }
}

/**
 * Which answer a field gets. `miss` is exactly the server's ENGINE_MISS set
 * (value_available, label_seen_unparsed, no_search_route), so the engine-miss
 * tile and the bar segments can never disagree.
 */
export function bucketOf(row: FieldMapRow): BucketId {
  if (row.state === 'populated') return 'shown'
  switch (row.resolution) {
    case 'absent_from_package':
      return 'absent'
    case 'derivable_when':
      return 'derivable'
    case 'needs_review':
      return 'review'
    case 'value_available':
    case 'label_seen_unparsed':
    case 'no_search_route':
      return 'miss'
    case 'desk_assigned':
      return 'desk'
    case 'not_applicable':
      return 'na'
    default:
      // no_package, unavailable, or a resolution this client does not know yet.
      return 'unchecked'
  }
}

export function countBuckets(rows: readonly FieldMapRow[]): BucketCounts {
  const counts = emptyCounts()
  for (const row of rows) counts[bucketOf(row)] += 1
  return counts
}

export type PageBar = {
  page: string
  total: number
  /** From the server's score, so the figure beside the bar is the score's own. */
  populated: number
  pct: number
  counts: BucketCounts
}

/** One bar per dossier page, then a closing bar for every field. */
export function pageBars(data: FieldMap): { pages: PageBar[]; all: PageBar } {
  const pages = data.score.byPage.map((p) => ({
    page: p.page,
    total: p.total,
    populated: p.populated,
    pct: p.pct,
    counts: countBuckets(data.fields.filter((r) => r.page === p.page)),
  }))
  const all: PageBar = {
    page: 'All fields',
    total: data.fields.length,
    populated: data.score.overall.populated,
    pct: data.score.overall.pct,
    counts: countBuckets(data.fields),
  }
  return { pages, all }
}

export type UnlockEntry = {
  field: string
  label: string
  page: string
  gate: boolean
  owner: FieldMapOwner | null
  /** Derived fields still blank that this input is one of the inputs of. */
  total: number
  /** Of those, the ones this input alone completes (no other input is also missing). */
  alone: number
  targets: { label: string; alone: boolean; alsoNeeds: string[] }[]
}

/**
 * The blank fields whose arrival would fill derived fields, ranked by how many.
 * Taken from each row's own `impact.blocks`, which the server builds from the
 * platform's derivation rules: this is a re-sort, not a new rule.
 */
export function unlockRanking(fields: readonly FieldMapRow[]): UnlockEntry[] {
  const out: UnlockEntry[] = []
  for (const r of fields) {
    const blocks = r.state === 'blank' ? r.impact?.blocks ?? [] : []
    if (blocks.length === 0) continue
    const targets = blocks.map((b) => ({ label: b.label, alone: b.alsoNeeds.length === 0, alsoNeeds: b.alsoNeeds }))
    out.push({
      field: r.field,
      label: r.label,
      page: r.page,
      gate: r.gate,
      owner: r.owner ?? null,
      total: targets.length,
      alone: targets.filter((t) => t.alone).length,
      targets,
    })
  }
  return out.sort(
    (a, b) =>
      b.total - a.total ||
      b.alone - a.alone ||
      Number(b.gate) - Number(a.gate) ||
      a.label.localeCompare(b.label),
  )
}

export type StatusFilter = '' | 'blank' | BucketId

export function matchesStatus(row: FieldMapRow, filter: StatusFilter): boolean {
  if (!filter) return true
  if (filter === 'blank') return row.state === 'blank'
  return bucketOf(row) === filter
}

export type SortKey = 'label' | 'page' | 'status' | 'holds'

export function holdsBack(row: FieldMapRow): number {
  return row.state === 'blank' ? row.impact?.blocks.length ?? 0 : 0
}

export function sortFields(rows: readonly FieldMapRow[], key: SortKey | null, dir: 1 | -1): FieldMapRow[] {
  if (!key) return [...rows]
  const rank = (r: FieldMapRow) => BUCKET_ORDER.indexOf(bucketOf(r))
  const cmp: Record<SortKey, (a: FieldMapRow, b: FieldMapRow) => number> = {
    label: (a, b) => a.label.localeCompare(b.label),
    page: (a, b) => a.page.localeCompare(b.page),
    status: (a, b) => rank(a) - rank(b),
    holds: (a, b) => holdsBack(a) - holdsBack(b),
  }
  // Array.prototype.sort is stable, so ties keep the catalog's own order.
  return [...rows].sort((a, b) => dir * cmp[key](a, b))
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/**
 * The page's one sentence. Every figure in it is a count of `fields[]`; a
 * bucket with nothing in it is not mentioned.
 */
export function headline(data: FieldMap, counts: BucketCounts = countBuckets(data.fields)): string {
  const total = data.fields.length
  if (total === 0) return 'The catalog returned no fields for this deal, so there is nothing to score.'
  const lead = `${counts.shown} of ${total} fields are on the dossier (${data.score.overall.pct.toFixed(1)}%).`
  const blanks: string[] = []
  if (counts.absent) blanks.push(`${counts.absent} not in the package`)
  if (counts.derivable) blanks.push(`${counts.derivable} derivable once inputs arrive`)
  if (counts.review) blanks.push(`${counts.review} waiting on a person`)
  if (counts.miss) blanks.push(`${counts.miss} ${plural(counts.miss, 'engine miss', 'engine misses')}`)
  if (counts.unchecked) blanks.push(`${counts.unchecked} not checked against a package`)
  if (counts.desk) blanks.push(`${counts.desk} set by the desk`)
  if (counts.na) blanks.push(`${counts.na} not applicable`)
  if (blanks.length === 0) return `${lead} Nothing is blank.`
  return `${lead} Blank: ${blanks.join(', ')}.`
}

/** The server's ten resolutions, in the words a reader without the package open can act on. */
export const RESOLUTION_LABEL: Record<FieldMapResolution, string> = {
  value_available: 'Stated in a source, not read',
  label_seen_unparsed: 'Labeled in a source, not parsed',
  no_search_route: 'Package not searched',
  needs_review: 'Awaiting a reviewer',
  derivable_when: 'Calculated once inputs arrive',
  absent_from_package: 'Not in the package',
  desk_assigned: 'Set by the desk',
  not_applicable: 'Not applicable',
  no_package: 'No package attached',
  unavailable: 'Package search failed',
}

export const OWNER_LABEL: Record<FieldMapOwner, string> = {
  us: 'Engineering',
  reviewer: 'Reviewer',
  inputs: 'Inputs',
  broker: 'Broker',
  desk: 'Desk',
}

/** The worklist's groups, in the order of who is closest to being able to act. */
export const GROUPS: { owner: FieldMapOwner; title: string; lead: string }[] = [
  { owner: 'broker', title: 'Ask the broker', lead: 'No document in the package states these.' },
  { owner: 'inputs', title: 'Provide the inputs', lead: 'A formula fills each one once the figures it needs are known.' },
  { owner: 'reviewer', title: 'A reviewer decides', lead: 'Sources disagree, or a value sits under a label several fields share.' },
  { owner: 'us', title: 'Engineering', lead: 'A source states it, or could not be searched. The engine is at fault, not the package.' },
]

/** What each owner's ask is called wherever a colour stands for "who has to move". */
export const OWNER_ASK: Record<FieldMapOwner, string> = {
  broker: 'Ask the broker',
  inputs: 'Provide the inputs',
  reviewer: 'A reviewer decides',
  us: 'Engineering',
  desk: 'The desk enters',
}

export const ACTOR_LABEL = {
  package: 'Read from the package',
  calculated: 'Calculated',
  person: 'Entered or confirmed by a person',
} as const

export function pctText(pct: number): string {
  return `${pct.toFixed(1)}%`
}

export function stamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString()
}

/** The "Origin or reason" cell: where a held value came from, or the one reason a blank is blank and who moves. */
export function rowReason(row: FieldMapRow): string {
  if (row.state === 'blank') {
    const why = `${RESOLUTION_LABEL[row.resolution ?? 'unavailable']}${row.owner ? ` · ${OWNER_LABEL[row.owner]}` : ''}`
    // A default is stored but is not a value anyone stated: say so first.
    return row.assumed ? `Default ${String(row.assumed.value)}, not counted · ${why}` : why
  }
  const held = row.origin
    ? `${ACTOR_LABEL[row.origin.actor]}${row.origin.source ? ` · ${row.origin.source}` : ''}`
    : `Held on the ${row.heldOn}`
  // A covenant term is the desk's: the sponsor's first reading until the desk sets its own.
  return row.ownership?.stamp ? `${row.ownership.stamp} · ${held}` : held
}
